import { Badge } from '@/components/UI';
import SummaryStat from './SummaryStat';
import { getSourceAuditBadgeLabel } from './helpers';
import type { SourceAuditReport, SourceAuditSummaryRow } from './types';

const SourceAuditMetricSummary = ({ row }: { row: SourceAuditSummaryRow }) => (
    <div className="rounded border border-zinc-800 bg-zinc-950/40 p-3">
        <div className="mb-3 flex items-center justify-between gap-2">
            <div className="font-medium text-white">{row.metric === 'options' ? 'Options' : 'Stock'}</div>
            <Badge variant={!row.auditFresh || row.activeBackendIssueRefs > 0 || row.externalNeedsReviewRows > 0 ? 'warning' : 'success'}>
                {getSourceAuditBadgeLabel(row)}
            </Badge>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-11">
            <SummaryStat label="Relevant" value={row.relevantRows} />
            <SummaryStat label="Unaudited" tone={row.unauditedRelevantRows > 0 ? 'bad' : 'good'} value={row.unauditedRelevantRows} />
            <SummaryStat label="Stale" tone={row.staleRows > 0 ? 'warn' : 'good'} value={row.staleRows} />
            <SummaryStat label="Ext Review" tone={row.externalNeedsReviewRows > 0 ? 'warn' : 'good'} value={row.externalNeedsReviewRows} />
            <SummaryStat label="Ext Stale" tone={row.externalStaleRows > 0 ? 'warn' : 'good'} value={row.externalStaleRows} />
            <SummaryStat label="Open" tone={row.openBackendIssueRefs > 0 ? 'bad' : 'good'} value={row.openBackendIssueRefs} />
            <SummaryStat label="Triaged" tone={row.triagedBackendIssueRefs > 0 ? 'warn' : 'neutral'} value={row.triagedBackendIssueRefs} />
            <SummaryStat
                label="Codex"
                tone={row.codexQueuedBackendIssueRefs > 0 ? 'bad' : 'neutral'}
                value={row.codexQueuedBackendIssueRefs}
            />
            <SummaryStat label="More Data" tone={row.needsMoreDataRows > 0 ? 'warn' : 'neutral'} value={row.needsMoreDataRows} />
            <SummaryStat label="Source Fix" tone={row.needsSourceFixRows > 0 ? 'bad' : 'good'} value={row.needsSourceFixRows} />
            <SummaryStat label="Active" tone={row.activeBackendIssueRefs > 0 ? 'warn' : 'good'} value={row.activeBackendIssueRefs} />
        </div>
    </div>
);

interface SourceReportSectionProps {
    isLoading: boolean;
    errorMessage: string | undefined;
    report: SourceAuditReport | undefined;
}

const SourceReportSection = ({ isLoading, errorMessage, report }: SourceReportSectionProps) => {
    if (isLoading) return <div className="text-sm text-zinc-500">Loading audit status...</div>;
    if (errorMessage) return <div className="rounded border border-red-900/70 bg-red-950/20 p-3 text-sm text-red-300">{errorMessage}</div>;
    if (!report) return null;

    return (
        <div className="grid gap-3">
            {report.summary.map((row) => (
                <SourceAuditMetricSummary key={row.metric} row={row} />
            ))}
        </div>
    );
};

export default SourceReportSection;
